import React, { FC, useEffect, useState } from 'react';
import { Product } from '@commercetools/frontend-domain-types/product/Product';
import {
  loadProductConfig,
  loadShowSecondImageOnHoverFromStorage,
  Subtitle,
  SubtitleProps,
} from '@loomstateorg/shared-frontend';
import { SwiperOptions } from 'swiper';
import { Slider, Typography } from '@loomstateorg/shared-frontend';
import { SustainabilityItem } from 'components/commercetools-ui/types';
import Wrapper from 'components/HOC/wrapper';
import { mediumDesktop, tablet } from '@loomstateorg/shared-frontend/utils/screensizes';
import { Reference } from 'types/reference';
import Link from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/content/link';
import Tile from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/product/product-tile';
import { CHIPOTLE_TILE_LOOK } from '../chipotle-look';
import useTrack from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/product/product-slider/useTrack';

export interface ProductSliderProps extends Partial<SwiperOptions> {
  products: Product[];
  title?: string;
  titleVariant?: 'xs' | 'sm' | 'lg';
  subline?: string;
  subtitleVariant?: SubtitleProps['variant'];
  ctaLabel?: string;
  ctaLink?: Reference;
  clearDefaultWrapperStyles?: boolean;
  slidesPerView?: number;
  disableProductQuickView?: boolean;
  disableProductWishlistButton?: boolean;
  disableProductVariants?: boolean;
  innerArrows?: boolean;
  solidArrows?: boolean;
  breakpoints?: any;
  onProductClick?: (product: Product) => void;
  classNames?: {
    title?: string;
  };
  variant?: 'normal' | 'cart';
  sustainability?: {
    certificationsItem?: SustainabilityItem;
    environmentalItem?: SustainabilityItem;
  };
  dataTestId?: string;
}

const ProductSlider: FC<ProductSliderProps> = ({
  products,
  title,
  subline,
  ctaLabel,
  ctaLink,
  onProductClick,
  titleVariant = 'lg',
  subtitleVariant = 'lg',
  clearDefaultWrapperStyles = false,
  disableProductQuickView = false,
  disableProductWishlistButton = false,
  disableProductVariants = false,
  innerArrows = true,
  solidArrows = true,
  breakpoints = {},
  classNames = {},
  sustainability,
  dataTestId,
  ...props
}) => {
  const { trackClick } = useTrack();

  const [showSecondImageOnHover, setShowSecondImageOnHover] = useState(false);

  useEffect(() => {
    const cachedShowSecondImageOnHover = loadShowSecondImageOnHoverFromStorage();
    if (cachedShowSecondImageOnHover !== null) setShowSecondImageOnHover(cachedShowSecondImageOnHover);

    loadProductConfig().then((config) => {
      setShowSecondImageOnHover(config.showSecondImageOnHover);
    });
  }, []);

  return (
    <div data-testid={dataTestId ? `${dataTestId}-section` : undefined}>
      <Wrapper clearDefaultStyles={true} className="mt-52">
        <div>
          <Typography
            data-testid={dataTestId ? `${dataTestId}-section-title` : undefined}
            className={`mb-12 text-24 uppercase md:text-36 ${classNames.title ?? ''}`}
          >
            {title}
          </Typography>
          {(subline || ctaLink) && (
            <div className="mt-8 flex items-center justify-between md:mt-16 lg:mt-14">
              {subline && (
                <Subtitle
                  subtitle={subline}
                  variant={subtitleVariant}
                  data-testid={dataTestId ? `${dataTestId}-section-subtitle` : undefined}
                />
              )}
              {ctaLink && (
                <div className="hidden lg:block" data-testid="view-all">
                  {/* testid-ignore: content Link doesn't forward data-testid; the view-all wrapper carries it */}
                  <Link target={ctaLink} withArrow>
                    <span className="text-secondary-black font-medium leading-[24px]">{ctaLabel}</span>
                  </Link>
                </div>
              )}
            </div>
          )}
          {ctaLink && (
            <div className="mt-20 block md:mt-16 lg:hidden" data-testid="view-all-mobile">
              {/* testid-ignore: content Link doesn't forward data-testid; the view-all-mobile wrapper carries it */}
              <Link target={ctaLink} withArrow>
                <span className="text-secondary-black font-medium leading-[24px]">{ctaLabel}</span>
              </Link>
            </div>
          )}
        </div>
        <div className="mt-20 md:mt-24 lg:mt-20">
          <div className="relative mt-6 w-full">
            <Slider
              slidesPerView={1.2}
              slidesPerGroup={1}
              dots={false}
              arrows
              innerArrows={innerArrows}
              solidArrows={solidArrows}
              nextButtonStyles={{ transform: 'translateY(-150%)' }}
              prevButtonStyles={{ transform: 'translateY(-150%)' }}
              allowTouchMove
              spaceBetween={16}
              breakpoints={{
                [tablet]: {
                  slidesPerView: 2.15,
                  spaceBetween: 25,
                  ...(breakpoints[tablet] ?? {}),
                },
                [mediumDesktop]: {
                  slidesPerView: 3,
                  slidesPerGroup: 3,
                  spaceBetween: 25,
                  ...(breakpoints[mediumDesktop] ?? {}),
                },
              }}
              {...props}
            >
              {products.map((product, index) => (
                <Tile
                  key={product.slug}
                  disableQuickView={disableProductQuickView}
                  disableWishlistButton={disableProductWishlistButton}
                  disableVariants={disableProductVariants}
                  product={product}
                  onClick={() => {
                    trackClick(product, index + 1);
                    onProductClick?.(product);
                  }}
                  sustainability={sustainability}
                  dataTestId={dataTestId ? `${dataTestId}-product` : undefined}
                  showSecondImageOnHover={showSecondImageOnHover}
                  {...CHIPOTLE_TILE_LOOK}
                />
              ))}
            </Slider>
          </div>
        </div>
      </Wrapper>
    </div>
  );
};

export default ProductSlider;
